import { useEffect, useState } from "react";
import { adminCreateWorkshop, adminGetDaySlots, getWorkshopKinds, toLocalDateStr } from "../../lib/hooks";
import type { SlotAvailability, WorkshopKind, WorkshopKindID } from "../../lib/types";

const RESOURCE_ORDER = ["stol", "kruh"];

// Native <input type="time"> renders as "12:30 PM" with an English system locale and
// wants every segment typed, so the form uses plain selects: 07:00–23:00 in 15-minute steps.
const TIME_OPTIONS: string[] = [];
for (let h = 7; h <= 23; h++) {
  for (const m of [0, 15, 30, 45]) {
    if (h === 23 && m > 0) break;
    TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

const hourOf = (t: string) => parseInt(t.slice(0, 2), 10);

/**
 * Hours (as slot start hours) in which every chosen resource has a generated slot
 * that day. A workshop may only run inside them: the calendar draws nothing else
 * and the backend refuses the rest.
 */
function coveredHours(slots: SlotAvailability[], resources: string[]): Set<number> {
  const perResource = resources.map((r) => new Set(slots.filter((s) => s.resource_type === r).map((s) => hourOf(s.start_time))));
  const covered = new Set<number>();
  for (const h of perResource[0] ?? []) {
    if (perResource.every((set) => set.has(h))) covered.add(h);
  }
  return covered;
}

/** Start times whose hour has a slot. */
function startOptions(covered: Set<number>): string[] {
  return TIME_OPTIONS.filter((t) => covered.has(hourOf(t)));
}

/** End times after `start` such that every hour the window touches has a slot. */
function endOptions(covered: Set<number>, start: string): string[] {
  if (!start) return [];
  return TIME_OPTIONS.filter((t) => {
    if (t <= start) return false;
    const last = t.endsWith(":00") ? hourOf(t) - 1 : hourOf(t);
    for (let h = hourOf(start); h <= last; h++) if (!covered.has(h)) return false;
    return true;
  });
}

/** "17:00" + 2 h, or the latest allowed end if the day closes earlier. */
function defaultEnd(start: string, ends: string[]): string {
  const i = TIME_OPTIONS.indexOf(start);
  const wanted = TIME_OPTIONS[Math.min(i + 8, TIME_OPTIONS.length - 1)];
  if (ends.includes(wanted)) return wanted;
  return ends[ends.length - 1] ?? "";
}

function resourceNames(resources: string[]): string {
  return resources
    .map((r) => RESOURCE_LABELS[r as keyof typeof RESOURCE_LABELS] ?? r)
    .join(" a ");
}
import { RESOURCE_LABELS } from "../../lib/types";

interface CreateWorkshopProps {
  onCreated?: () => void;
}

export function CreateWorkshop({ onCreated }: CreateWorkshopProps = {}) {
  const [kinds, setKinds] = useState<WorkshopKind[]>([]);
  const [kind, setKind] = useState<WorkshopKindID | "">("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [capacity, setCapacity] = useState("");
  const [price, setPrice] = useState("");
  const [usesResources, setUsesResources] = useState<string[]>(["stol"]);
  const [daySlots, setDaySlots] = useState<SlotAvailability[] | null>(null); // null = no date / loading

  // Resources are independent toggles; the last one can't be switched off.
  const toggleResource = (res: string) => {
    setUsesResources((prev) => {
      if (prev.includes(res)) {
        return prev.length === 1 ? prev : prev.filter((r) => r !== res);
      }
      return RESOURCE_ORDER.filter((r) => r === res || prev.includes(r));
    });
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    getWorkshopKinds().then(({ data, error: err }) => {
      if (data) setKinds(data);
      else setError(err?.message ?? "Nepodarilo sa načítať druhy workshopov.");
    });
  }, []);

  // Picking a kind prefills the name and price; both stay editable afterwards.
  const selectKind = (id: WorkshopKindID | "") => {
    setKind(id);
    const k = kinds.find((x) => x.id === id);
    if (k) {
      setTitle(k.label);
      setPrice(String(k.default_price_cents / 100));
    }
  };

  // The day's slots decide which times are offered; reload when the date changes.
  useEffect(() => {
    if (!date) {
      setDaySlots(null);
      return;
    }
    let cancelled = false;
    setDaySlots(null);
    adminGetDaySlots(date).then(({ data }) => {
      if (!cancelled) setDaySlots(data ?? []);
    });
    return () => { cancelled = true; };
  }, [date]);

  const covered = coveredHours(daySlots ?? [], usesResources);
  const today = toLocalDateStr(new Date());
  const nowHHMM = new Date().toTimeString().slice(0, 5);
  // Today only times still ahead; earlier days are blocked by the date input's min.
  const starts = startOptions(covered).filter((t) => date !== today || t > nowHHMM);
  const ends = endOptions(covered, startTime);

  // Drop a chosen time that the day (or resource choice) no longer allows.
  useEffect(() => {
    if (startTime && !starts.includes(startTime)) setStartTime("");
    if (endTime && !ends.includes(endTime)) setEndTime("");
  }, [starts.join(","), ends.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking a start sets the end to two hours later (or as late as the day allows).
  const selectStart = (t: string) => {
    setStartTime(t);
    const nextEnds = endOptions(covered, t);
    if (t && (!endTime || !nextEnds.includes(endTime))) setEndTime(defaultEnd(t, nextEnds));
  };

  const noSlotsThatDay = date !== "" && daySlots !== null && covered.size === 0;

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    if (!kind) {
      setError("Vyberte druh workshopu.");
      setLoading(false);
      return;
    }
    if (!title.trim()) {
      setError("Názov je povinný.");
      setLoading(false);
      return;
    }
    if (!date) {
      setError("Dátum je povinný.");
      setLoading(false);
      return;
    }
    if (!startTime || !endTime) {
      setError("Začiatok a koniec sú povinné.");
      setLoading(false);
      return;
    }
    if (startTime >= endTime) {
      setError("Začiatok musí byť pred koncom.");
      setLoading(false);
      return;
    }
    const capacityNum = parseInt(capacity, 10);
    if (!capacity || isNaN(capacityNum) || capacityNum <= 0) {
      setError("Kapacita musí byť väčšia ako 0.");
      setLoading(false);
      return;
    }
    const priceNum = parseFloat(price.replace(",", "."));
    if (price === "" || isNaN(priceNum) || priceNum < 0) {
      setError("Zadajte cenu (0 € je povolené).");
      setLoading(false);
      return;
    }

    const { data, error: err } = await adminCreateWorkshop({
      workshopKind: kind,
      title: title.trim(),
      date,
      startTime,
      endTime,
      capacity: capacityNum,
      priceCents: Math.round(priceNum * 100),
      usesResources,
    });

    if (err) {
      setError(err.message);
    } else if (data) {
      const d = new Date(data.date + "T00:00:00");
      const dayStr = d.toLocaleDateString("sk-SK", { weekday: "short", day: "numeric", month: "numeric" });
      let msg = `Workshop "${data.title}" vytvorený na ${dayStr} ${data.start_time?.slice(0, 5)}–${data.end_time?.slice(0, 5)}`;
      if (data.blocked_count > 0) {
        msg += ` — ${data.blocked_count} slotov (${resourceNames(data.uses_resources)}) zablokovaných`;
      }
      setSuccess(msg);
      setKind("");
      setTitle("");
      setDate("");
      setStartTime("");
      setEndTime("");
      setCapacity("");
      setPrice("");
      onCreated?.();
    }
    setLoading(false);
  };

  return (
    <div className="admin-card">
      <h3>Vytvoriť workshop</h3>

      <div className="form-field">
        <label>Druh:</label>
        <select value={kind} onChange={(e) => selectKind(e.target.value as WorkshopKindID | "")}>
          <option value="">— vyberte druh —</option>
          {kinds.map((k) => (
            <option key={k.id} value={k.id}>{k.label}</option>
          ))}
        </select>
        <span className="form-hint">
          Druh určuje, ktorý darčekový poukaz na workshop platí. Názov a cena sa predvyplnia, dajú sa upraviť.
        </span>
      </div>

      <div className="form-field">
        <label>Názov:</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="napr. Glazovanie pre začiatočníkov"
        />
      </div>

      <div className="form-field">
        <label>Využíva zdroje:</label>
        <div className="resource-picker">
          <button
            type="button"
            className={`resource-pick-btn ${usesResources.includes("stol") ? "active" : ""}`}
            onClick={() => toggleResource("stol")}
          >
            {RESOURCE_LABELS.stol}
          </button>
          <button
            type="button"
            className={`resource-pick-btn ${usesResources.includes("kruh") ? "active" : ""}`}
            onClick={() => toggleResource("kruh")}
          >
            {RESOURCE_LABELS.kruh}
          </button>
        </div>
        <span className="form-hint">
          Sloty pre {resourceNames(usesResources)} v čase workshopu budú automaticky zablokované. Aspoň jeden zdroj musí byť vybraný.
        </span>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Dátum:</label>
          <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label>Začiatok:</label>
          <select value={startTime} onChange={(e) => selectStart(e.target.value)} disabled={!date || starts.length === 0}>
            <option value="">—</option>
            {starts.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label>Koniec:</label>
          <select value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={!startTime}>
            <option value="">—</option>
            {ends.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
      {!date && <span className="form-hint">Časy sa ponúknu po výbere dátumu, len v hodinách, na ktoré sú vygenerované sloty.</span>}
      {noSlotsThatDay && (
        <div className="form-error">
          Na tento deň nie sú vygenerované sloty pre {resourceNames(usesResources)}. Najprv vygeneruj týždeň v záložke „Generovať sloty“.
        </div>
      )}

      <div className="form-row">
        <div className="form-field">
          <label>Kapacita:</label>
          <input
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            min={1}
            placeholder="napr. 6"
          />
        </div>
        <div className="form-field">
          <label>Cena (€):</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            min={0}
            step={0.5}
            placeholder="napr. 40"
          />
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleCreate}
        disabled={loading}
      >
        {loading ? "Vytváram..." : "Vytvoriť workshop"}
      </button>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}
    </div>
  );
}
